import { Fragment } from "react";
import { Outlet, Navigate, type RouteObject } from "infrastructure/router";
import { WebRoutes } from "consts/enpoints/web";
import { ApiRouteTypes } from "consts/enpoints/api";
import routes from "consts/routes";
import { ECommerceOnboarding, ECommerceOnboardingStep } from "consts/ecommerce";
import { RedirectIfAuthenticated } from "ui/layouts/Auth";
import { SignInOutlet } from "ui/layouts/SignIn";
import Login from "ui/screens/Login";
import Verify2FA from "ui/screens/Verify2FA";
import SendResetPasswordLink from "ui/screens/SendResetPasswordLink";
import ChangePassword from "ui/screens/ChangePassword";
import NotAuthorized from "ui/molecules/NotAuthorized";
import Transactions from "ui/screens/Transactions";
import UserManagement from "ui/screens/UserManagement";
import StatisticDashboard from "ui/screens/StatisticDashboard";
import Home, {
    ECommerce,
    ECommercePlatforms,
    ECommercePlatformSetup
} from "ui/screens/Home";
import {
    WhitelistedConsumers,
    ConsumersOutlet
} from "ui/screens/Consumers";
import Finance from "ui/screens/Finance";
import Profile from "ui/screens/Profile";
import Support from "ui/screens/Support";
import {
    Merchant,
    Onboarding
} from "ui/screens/Onboarding";
import PaymentMethods from "ui/screens/PaymentMethods";
import MerchantPayouts from "ui/screens/MerchantPayouts";

const config: RouteObject[] = [
    {
        path: WebRoutes.Root,
        id: ApiRouteTypes.GetSettings,
        HydrateFallback: Fragment,
        lazy: () => import('ui/screens/Dashboard'),
        shouldRevalidate: () => false,
        children: [
            {
                ...routes[WebRoutes.Root],
                element: (
                    <Home
                        {...routes[WebRoutes.Root]}
                    />
                ),
                children: [
                    {
                        index: true,
                        element: (
                            <ECommerce />
                        )
                    },
                    {
                        path: WebRoutes.ECommerce.Platforms,
                        element: (
                            <ECommercePlatforms />
                        )
                    },
                    {
                        path: [WebRoutes.ECommerce.Platforms, ':platform', ':step?'].join('/'),
                        element: (
                            <ECommercePlatformSetup />
                        )
                    }
                ]
            },
            {
                ...routes[WebRoutes.Dashboard],
                element: (
                    <StatisticDashboard
                        {...routes[WebRoutes.Dashboard]}
                    />
                )
            },
            {
                ...routes[WebRoutes.PaymentMethods],
                element: (
                    <PaymentMethods
                        {...routes[WebRoutes.PaymentMethods]}
                    />
                )
            },
            {
                ...routes[WebRoutes.Transactions],
                element: (
                    <Transactions
                        {...routes[WebRoutes.Transactions]}
                    />
                )
            },
            {
                ...routes[WebRoutes.Consumers.Index],
                element: (
                    <ConsumersOutlet />
                ),
                children: [
                    {
                        index: true,
                        element: (
                            <Navigate
                                replace
                                to={routes[WebRoutes.Consumers.WhiteListed].path}
                            />
                        )
                    },
                    {
                        ...routes[WebRoutes.Consumers.WhiteListed],
                        element: (
                            <WhitelistedConsumers
                                {...routes[WebRoutes.Consumers.WhiteListed]}
                            />
                        )
                    }
                ]
            },
            {
                ...routes[WebRoutes.Finance.Index],
                element: (
                    <Outlet />
                ),
                children: [
                    {
                        index: true,
                        element: (
                            <Navigate
                                replace
                                to={routes[WebRoutes.Finance.Settlements].path}
                            />
                        )
                    },
                    {
                        ...routes[WebRoutes.Finance.Settlements],
                        element: (
                            <Finance
                                {...routes[WebRoutes.Finance.Settlements]}
                            />
                        )
                    },
                    {
                        ...routes[WebRoutes.Finance.SummaryFiles],
                        element: (
                            <MerchantPayouts />
                        )
                    }
                ]
            },
            {
                ...routes[WebRoutes.Profile],
                element: (
                    <Profile
                        {...routes[WebRoutes.Profile]}
                    />
                )
            },
            {
                ...routes[WebRoutes.Users],
                element: (
                    <UserManagement
                        {...routes[WebRoutes.Users]}
                    />
                )
            },
            {
                ...routes[WebRoutes.Support],
                element: (
                    <Support
                        {...routes[WebRoutes.Support]}
                    />
                )
            },
            {
                path: "*",
                element: (
                    <NotAuthorized />
                )
            }
        ]
    },
    {
        path: WebRoutes.Login.Index,
        element: (
            <RedirectIfAuthenticated>
                <SignInOutlet />
            </RedirectIfAuthenticated>
        ),
        children: [
            {
                index: true,
                element: (
                    <Login />
                )
            },
            {
                path: WebRoutes.Login.Verify2FA,
                element: (
                    <Verify2FA />
                )
            },
            {
                path: WebRoutes.Login.SendResetLink,
                element: (
                    <SendResetPasswordLink />
                )
            }
        ]
    },
    {
        path: WebRoutes.ChangePassword,
        element: (
            <RedirectIfAuthenticated>
                <SignInOutlet />
            </RedirectIfAuthenticated>
        ),
        children: [
            {
                index: true,
                element: (
                    <ChangePassword />
                )
            }
        ]
    },
    {
        path: WebRoutes.PreApplication,
        async lazy() {
            const { PreApplication: Component } = await import('ui/screens/PreApplication');

            return {
                Component
            };
        }
    },
    {
        path: WebRoutes.Onboarding.Index,
        id: ApiRouteTypes.GetOnboardingsApplications,
        HydrateFallback: Fragment,
        lazy: () => import('ui/screens/Onboarding'),
        children: [
            {
                index: true,
                element: (
                    <Onboarding />
                )
            },
            {
                path: ECommerceOnboarding.Merchant,
                element: (
                    <Outlet />
                ),
                children: [
                    {
                        index: true,
                        element: (
                            <Merchant.OnboardingGettingStarted />
                        )
                    },
                    {
                        path: 'step',
                        element: (
                            <Merchant.Onboarding />
                        ),
                        children: [
                            {
                                index: true,
                                element: (
                                    <Merchant.OnboardingInitialStepRedirect />
                                )
                            },
                            {
                                path: `${ECommerceOnboardingStep[ECommerceOnboarding.Merchant].BusinessInformation}`,
                                lazy: async () => import('ui/screens/Onboarding/Merchant/BusinessInformation')
                            },
                            {
                                path: `${ECommerceOnboardingStep[ECommerceOnboarding.Merchant].BankingAndFinance}`,
                                lazy: async () => import('ui/screens/Onboarding/Merchant/BankingAndFinance')
                            },
                            {
                                path: `${ECommerceOnboardingStep[ECommerceOnboarding.Merchant].MediaAndComplianceCheck}`,
                                lazy: async () => import('ui/screens/Onboarding/Merchant/MediaAndComplianceCheck')
                            },
                            {
                                path: `${ECommerceOnboardingStep[ECommerceOnboarding.Merchant].UBOIdentityVerification}`,
                                lazy: async () => import('ui/screens/Onboarding/Merchant/UBOIdentityVerification')
                            },
                            {
                                path: `${ECommerceOnboardingStep[ECommerceOnboarding.Merchant].PaymentMethods}`,
                                lazy: async () => import('ui/screens/Onboarding/Merchant/PaymentMethods')
                            }
                        ]
                    },
                    {
                        path: 'complete',
                        element: (
                            <Merchant.OnboardingComplete />
                        )
                    }
                ]
            }
        ]
    }
];

export default config;
